import { KitType } from '@repo/types';
import { Button, ButtonColors, TextColor, Typography } from '@repo/ui';
import background from 'assets/images/summary/first-look/end_background.svg';
import c from 'classnames';
import { useAnalytics } from 'contexts/analytics/AnalyticsProvider';
import { useCurrentKitAndTinyAccount } from 'hooks/useCurrentKitAndTinyAccount';
import React, { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { setModalOpened } from 'store/modal';
import { SLIDE_BACKGROUND_COLORS } from '../slideBackgroundColors';
import styles from './End.module.css';
import slideStyles from './Slides.module.css';

const METRICS_GUT = [
  'Bifidobacterium',
  'Akkermansia',
  'Faecalibacterium',
  'Lactobacillaceae',
  'Abundance index',
  'Enterobacteriaceae',
  'Escherichia coli',
  'Salmonella enterica',
  'Streptococcus',
  'Candida',
];

const METRICS_VAG = [
  'Lactobacillus',
  'Gardnerella vaginalis',
  'Fannyhessea vaginae',
  'Chlamydia trachomatis',
  'Ureaplasma urealyticum',
  'Candida albicans',
];

const RowItem = ({ metric }: { metric: string }) => {
  return (
    <div className={styles.rowItem}>
      <Typography variant='body-s'>{metric}</Typography>
    </div>
  );
};

export const End: React.FC = () => {
  const dispatch = useDispatch();
  const { trackEvent } = useAnalytics();
  const { currentKit } = useCurrentKitAndTinyAccount();
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const useMetrics =
    currentKit?.sampling_type === KitType.VAGINAL ? METRICS_VAG : METRICS_GUT;
  const duplicatedMetrics = [
    ...useMetrics,
    ...useMetrics,
    ...useMetrics,
    ...useMetrics,
  ];

  const modalIdentifier = {
    name: 'firstLook',
    props: {},
  };

  const dismissModal = () => {
    dispatch(setModalOpened(modalIdentifier));
  };

  const handleViewFullReport = () => {
    trackEvent('First Look', {
      event_type: 'button_clicked',
      button_text: 'View Full Report',
      slide_id: 'end',
      action: 'view_full_report',
    });
    dismissModal();
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    let animationId: number;
    let scrollPosition = 0;
    const scrollSpeed = 0.3;
    const itemHeight = 48;
    const totalHeight = useMetrics.length * itemHeight;

    const animate = () => {
      scrollPosition += scrollSpeed;

      if (scrollPosition >= totalHeight) {
        scrollPosition = 0;
      }

      container.style.transform = `translateY(-${scrollPosition}px)`;
      animationId = requestAnimationFrame(animate);
    };

    animationId = requestAnimationFrame(animate);

    return () => {
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  }, []);

  return (
    <div
      className={c(slideStyles.slideWrapper, styles.slideContainer)}
      style={{ backgroundColor: SLIDE_BACKGROUND_COLORS.end }}
    >
      <div className={styles.contentContainer}>
        <div className={styles.textContainer}>
          <Typography
            variant='heading-2xl'
            color={TextColor.DARKPURPLE}
            weight='medium'
          >
            Wait, there’s more!
          </Typography>
          <Typography variant='body-m'>
            Dive into the full report for more details, insights &
            recommendations.
          </Typography>
        </div>
        <div className={styles.imagesContainer}>
          <img
            src={background}
            alt='Results background'
            className={styles.backgroundImage}
          />
          <div className={styles.scrollWrapper}>
            <div className={styles.scrollViewport}>
              <div className={styles.rowItemContainer} ref={scrollContainerRef}>
                {duplicatedMetrics.map((metric, index) => (
                  <RowItem key={index} metric={metric} />
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className={styles.buttonsContainer}>
          <Button
            variant='solid'
            color={ButtonColors.PURPLE}
            onClick={handleViewFullReport}
          >
            Dive into your report
          </Button>
        </div>
      </div>
    </div>
  );
};
